/**
 * Brand tokens — the single source of truth for every colour and typeface in the platform.
 *
 * This is the ONLY file permitted to contain raw colour literals. An ESLint rule
 * (`no-restricted-syntax`, see eslint.config.js) rejects hex, rgb(), and hsl() everywhere else, so
 * a hardcoded colour fails CI rather than quietly shipping.
 *
 * Four consumers read this module, which is why it is TypeScript rather than a stylesheet:
 *
 *   apps/web + apps/site → `emitCssCustomProperties()` generates theme.generated.css for Tailwind
 *   email templates      → React Email needs literal inline hex; mail clients ignore custom props
 *   print packs          → @react-pdf/renderer takes JS style objects
 *   the brand marks      → CountBird and Wall inline their SVG and read fills from here
 *
 * Because all four read the same module they cannot drift apart. Changing the palette is an edit
 * to `primitives` below and nothing else.
 *
 * CANONICAL SOURCE: docs/BRAND-BOARD.md (v2, "Navy & Chalk"), applied in docs/UI-UX-SPEC.md §2.
 * Where this file departs from those two documents it says so, with the measurement that forced it.
 */

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Navy & Chalk. A single deep navy carries every ground — heroes, rails, banners — against a warm
 * chalk page, and exactly one red does every action.
 *
 * The warm/cool tension is the character of the palette: the neutrals are warm (chalk, sand, light)
 * and the ink is cool (navy). Flattening both to one temperature is the fastest way to make this
 * look like every other admin tool.
 */
export const primitives = {
  /**
   * The navy. `deep` and `ink` are the SAME colour — the brand board says so explicitly, and it is
   * the reason the palette holds together: body text and hero panels are one value, so a heading
   * on chalk and a panel behind it are visibly the same family.
   */
  navy: {
    deep: '#1E2C42',
    /** Hover and pressed on deep, and the footer. */
    deepD: '#16212F',
    /**
     * One step up from the navy ground. The wall's tiles on a deep panel, and the seating board's
     * table lines — the only places something must be visible ON navy without being text.
     */
    lift: '#2A3A52',
  },

  /** Warm neutrals. Grounds, rules and inset panels. */
  warm: {
    /** The page. */
    chalk: '#F4F2EC',
    /** Borders, rules, muted fills. */
    light: '#EBE7DD',
    /** Table headers, inset panels, seat cards, and the wall's tiles on a light ground. */
    sand: '#E2DDD0',
    /** Input borders and secondary-button borders. Cooler than `light` on purpose. */
    stroke: '#C9CBC4',
    /** Cards. */
    paper: '#FFFFFF',
  },

  /**
   * The one accent, hue ~4.
   *
   * Unlike the clay it replaces, this red CAN carry text: 4.74:1 on the chalk page. `signalD` is
   * the darker form for links and for sitting on the signal tint. `onSignal` is the near-white that
   * goes on a signal fill — 4.99:1, and it is not pure white, which would be 4.5 exactly and leave
   * no room for a rounding argument.
   */
  signal: {
    base: '#B8453C',
    hover: '#A33B33',
    d: '#A33B33',
    on: '#FFF6F4',
    tint: '#F7E4E2',
  },

  /**
   * Text. Three steps, and the third one is corrected — see below.
   */
  ink: {
    /** Body text and headings. Identical to `navy.deep`, deliberately. */
    base: '#1E2C42',
    /** Secondary prose. The lightest colour allowed on a sentence somebody has to read. */
    two: '#46566C',
    /**
     * Labels, eyebrows, chip text, disabled. NEVER running text.
     *
     * CORRECTED from the brand board's `#6D7C8E`, which is 3.81:1 on the chalk page.
     *
     * Both documents exempt this pair on the grounds that it "clears the large-text threshold".
     * It does not, because the threshold has a SIZE attached — 24px, or 18.66px bold — and the
     * spec's own type scale sets eyebrows at 11px and micro text at 11.5–12.5px. A token being
     * designated for labels does not earn the allowance; the type has to actually be that big.
     *
     * This project has made this exact mistake once already. The previous palette's label grey sat
     * in the 3:1 group on precisely this reasoning and was certified at 4.13:1 for two months
     * before an accessibility scan found it. The fix then was three steps darker, and so is this:
     * 4.58 on chalk, 5.12 on a paper card. The hierarchy survives — it is still plainly lighter
     * than `ink.two` — and the change is imperceptible side by side.
     *
     * It does NOT clear 4.5 on `sand` (3.78) or `light` (4.15), and it is not sanctioned there.
     * Table headers take `ink.base` on sand (10.37), which is what the spec's own table pairs.
     */
    three: '#626F7F',
  },

  /**
   * Text on a `deep` panel. Not interchangeable with the ordinary ink tokens — `ink.two` on navy
   * is 1.6:1, which is the mistake the spec calls out in the mockups.
   */
  onDeep: {
    base: '#F4F2EC',
    two: '#C6CEDA',
    /** Labels on navy. Never prose. 4.85:1 — it clears the prose bar anyway. */
    three: '#8B99AB',
  },

  /**
   * Status only, never decoration. If it is coloured, it means something.
   *
   * There is no amber, gold or brass in this palette and there must not be one — the brand board
   * deletes the Build Spec's `--brass` rather than deprecating it. "Waiting on you" is `signal`.
   */
  status: {
    sage: '#3D6B58',
    /** The same status as a fill or a bar, where contrast against text is moot. */
    sageFill: '#4C7F69',
    /**
     * Broken, late, destructive, forfeited.
     *
     * Deliberately much deeper than `signal`. The previous palette's rust and its accent differed
     * by a ratio of 1.76 — indistinguishable in a chip, so a destructive button and a "needs you"
     * badge looked alike. This one cannot be mistaken for the accent.
     */
    rust: '#7E2A24',
  },

  /**
   * Status tints. Each has EXACTLY ONE text token that may sit on it, named in the semantic layer
   * alongside. Mixing them is the most common way a tinted chip ends up unreadable.
   */
  tint: {
    sage: '#E6EFE9',
    signal: '#F7E4E2',
    deep: '#E8EBF0',
    rust: '#F6E3E1',
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Semantic layer                                                              */
/* -------------------------------------------------------------------------- */

/**
 * What components actually reference. Never reach past this into `primitives` from a component —
 * that is exactly the coupling that makes a rebrand expensive.
 *
 * The names are the BRAND BOARD'S names. That is a change: the previous set used role names
 * (`primary`, `accentFill`, `surfaceSunken`) while every specification talked about `--deep`,
 * `--signal` and `--sand`. Anybody building from the spec wrote the spec's words, and those classes
 * silently did nothing — four pages shipped that way. The code and the documents now use one
 * vocabulary so that cannot happen again.
 */
export interface SemanticPalette {
  /* --- grounds ----------------------------------------------------------- */
  /** The page. */
  chalk: string;
  /** Cards. */
  paper: string;
  /** Inset panels, table headers, seat cards. */
  sand: string;
  /** Borders, rules, muted fills. */
  light: string;
  /** Hairlines, dividers, decorative rules. NOT the edge of anything you can operate. */
  stroke: string;
  /**
   * THE EDGE OF A CONTROL — an input, a select, a secondary button.
   *
   * Split out from `stroke`, which the brand board assigns both jobs and which cannot do the
   * second one. `--stroke` measures 1.64:1 on paper, 1.46 on chalk and 1.21 on sand: fine for a
   * hairline, and far under the 3:1 that WCAG 1.4.11 requires of a UI component's boundary. Every
   * input in the product had an effectively invisible edge.
   *
   * This is the label grey, which is the lightest value that clears 3:1 on all three grounds —
   * 4.90 on paper, 4.58 on chalk, 3.78 on sand. `#8E9288` was tried and only clears on paper.
   *
   * It is visibly darker than what it replaces. That is the point: a box you can type in should
   * look like a box you can type in.
   */
  controlBorder: string;

  /* --- text on light grounds --------------------------------------------- */
  ink: string;
  /** Secondary prose. The lightest permitted on running text. */
  ink2: string;
  /** Labels, eyebrows, disabled. NOT running text. */
  ink3: string;

  /* --- the navy ground and what sits on it -------------------------------- */
  /** Panels, hero fills, active nav, rails. */
  deep: string;
  deepHover: string;
  /** Headings on a deep panel. */
  onDeep: string;
  /** Body text on a deep panel. */
  onDeep2: string;
  /** Labels on a deep panel. Never prose. */
  onDeep3: string;

  /* --- the one accent ----------------------------------------------------- */
  /** Primary action, live, won, waiting on you. */
  signal: string;
  signalHover: string;
  /** Signal as text or a link on a light ground. */
  signalD: string;
  /** The near-white that sits on a signal fill. */
  onSignal: string;

  /* --- status ------------------------------------------------------------- */
  /** Good, on time, confirmed, paid, delivered. */
  sage: string;
  /** The same status as a fill or a bar. */
  sageFill: string;
  /** Broken, late, destructive, forfeited. */
  rust: string;

  /* --- tints, each with its one permitted text colour ---------------------- */
  tintSage: string;
  tintSignal: string;
  tintDeep: string;
  tintRust: string;
  /** The only text colour permitted on `tintSignal`. */
  onTintSignal: string;

  /* --- one step up from the navy ground ------------------------------------ */
  /** The seating board's table surface. */
  felt: string;
  /**
   * The only thing that reads ON navy without being text: the Wall's tiles on a deep panel, and the
   * seating board's table lines. One value, because they are the same job — the brand board sets
   * the wall's navy tile at exactly "one step up from the ground".
   */
  deepLift: string;

  /* --- focus --------------------------------------------------------------- */
  /**
   * The keyboard focus ring, and it is TWO tokens rather than one because a ring has to be visible
   * against whatever it lands on.
   *
   * UI-UX-SPEC §2 specifies a `--deep` focus ring. On the chalk page that is 12.55:1 and excellent.
   * On the navy rail it is **1.00:1** — the same colour as the ground, an invisible focus ring on
   * the surface a keyboard user navigates most. `--signal` is no good there either at 2.65:1.
   *
   * So `focus` is the ring for light surfaces and `focusOnDeep` is the ring for navy ones; the rail
   * overrides the custom property for everything inside it. The previous palette hit this exact
   * problem with a clay ring and solved it the same way. Found by measuring, which is the only way.
   */
  focus: string;
  /** The ring on a deep panel — the rail, hero cards, the seating board's felt. */
  focusOnDeep: string;
}

const p = primitives;

/**
 * The application palette, aligned to docs/BRAND-BOARD.md and UI-UX-SPEC §2.
 *
 * There is no dark variant. Neither document describes one, and deriving a dark navy palette would
 * mean inventing eleven colours nobody has approved — see DECISIONS.md. When one is specified it
 * comes back as a second object here and two blocks in the emitter.
 */
export const light: SemanticPalette = {
  chalk: p.warm.chalk,
  paper: p.warm.paper,
  sand: p.warm.sand,
  light: p.warm.light,
  stroke: p.warm.stroke,
  controlBorder: p.ink.three,

  ink: p.ink.base,
  ink2: p.ink.two,
  ink3: p.ink.three,

  deep: p.navy.deep,
  deepHover: p.navy.deepD,
  onDeep: p.onDeep.base,
  onDeep2: p.onDeep.two,
  onDeep3: p.onDeep.three,

  signal: p.signal.base,
  signalHover: p.signal.hover,
  signalD: p.signal.d,
  onSignal: p.signal.on,

  sage: p.status.sage,
  sageFill: p.status.sageFill,
  rust: p.status.rust,

  tintSage: p.tint.sage,
  tintSignal: p.tint.signal,
  tintDeep: p.tint.deep,
  tintRust: p.tint.rust,
  /** `--signal-d on --tint-signal`, the pairing the spec names. 5.31:1. */
  onTintSignal: p.signal.d,

  felt: p.navy.deep,
  deepLift: p.navy.lift,

  focus: p.navy.deep,
  focusOnDeep: p.onDeep.base,
};

/* -------------------------------------------------------------------------- */
/* Radius                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * CORNER RADIUS IS A BRAND PROPERTY, and it had no single source of truth — which is exactly why it
 * drifted to Tailwind's defaults everywhere.
 *
 * Colour has had one token file since day one and is spotless. Radius had nothing to reach for, so
 * screens reached for `rounded-md` (6px) 185 times and `rounded` (4px) 59 times, while the brand's
 * own 5px card corner was typed out by hand as `rounded-[5px]` in 37 places. The sign-in card alone
 * carried three different radii, with a 6px button sitting directly under a 4px input at identical
 * width — a mismatch you can see without measuring.
 *
 * The values are the brand board's: chips 2px, controls 4px, cards 5px, hero panels 6px.
 */
export const radius = {
  /** Status chips. Nearly square, so a chip never reads as a button. */
  chip: '2px',
  /** The default — inputs, buttons, small controls. */
  base: '4px',
  /** Cards and panels. The brand's own corner. */
  card: '5px',
  /** Hero panels and the largest surfaces. */
  panel: '6px',
} as const;

/* -------------------------------------------------------------------------- */
/* Typography                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Two faces. An editorial serif for headings, a sans for everything a user operates.
 *
 * Newsreader and Libre Franklin, per BRAND-BOARD.md and UI-UX-SPEC §2. Inter, Roboto, Arial and
 * Fraunces are named as forbidden primaries.
 *
 * This REPLACES Gilda Display, which was approved 2026-08-23 for its family resemblance to the
 * parent company's own heading face. The brand board supersedes that. See DECISIONS.md.
 */
/**
 * SINGLE quotes around family names, not double, and it is not a style preference.
 *
 * Email templates inline these into a `style="…"` attribute. A double quote inside a double-quoted
 * HTML attribute ends the attribute, so `font-family:"Libre Franklin"` silently truncates the whole
 * declaration and every email falls back to the browser's default serif. Single quotes are equally
 * valid CSS and survive the trip. There is a test for it.
 */
export const type = {
  /** Page titles, email headers, print packs. */
  display: "'Newsreader', Georgia, 'Times New Roman', serif",
  /** Everything else — every screen, every surface. */
  ui: "'Libre Franklin', 'Helvetica Neue', Arial, sans-serif",
  /** Alias: body and interface are the same face. Kept so callers need not care. */
  body: "'Libre Franklin', 'Helvetica Neue', Arial, sans-serif",
  /**
   * SYSTEM FACES ONLY, and that is the decision rather than an oversight.
   *
   * This used to name JetBrains Mono, which was never in `webFonts`, never requested, and absent
   * from the brand board — a declared brand face that no browser could ever have shown. It was the
   * same shape of bug as the display face, one step behind it, waiting.
   *
   * Two surfaces use it: the CSV paste box and the email-template preview. Neither is a brand
   * moment, and neither justifies a third webfont on every page load, so the named face is gone
   * instead of added. What is left is what was actually rendering all along.
   */
  mono: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
} as const;

/**
 * Google Fonts families to request. The only external host the platform loads assets from.
 *
 * Newsreader at 300 and 400: 300 is the marketing display size only, 400 everything else.
 */
export const webFonts = [
  'Newsreader:wght@300;400',
  'Libre+Franklin:wght@400;500;600;700',
] as const;

export const googleFontsHref = `https://fonts.googleapis.com/css2?${webFonts
  .map((f) => `family=${f}`)
  .join('&')}&display=swap`;

/* -------------------------------------------------------------------------- */
/* Elevation                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * THE ONLY SHADOW IN THE PRODUCT.
 *
 * UI-UX-SPEC §2 allows it on a dragged or focused element and nowhere else, so that when something
 * lifts it MEANS something is moving. A page of softly floating cards spends that signal on nothing
 * and leaves the seating board with no way to say "you are holding this".
 *
 * It is here rather than written into a component for the usual reason: it is a colour, and the
 * colour rule would reject it anywhere else. The spec states it as rgba(30,44,66,.10) — the navy
 * at 10% — which is `navy.deep` with an alpha suffix.
 */
export const shadows = {
  lifted: `0 2px 14px ${primitives.navy.deep}1A`,
} as const;

/* -------------------------------------------------------------------------- */
/* Emission                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Splits before capitals AND before digits, so `ink2` becomes `ink-2` and `onDeep3` becomes
 * `on-deep-3` — the exact names the specifications use. Without the digit case the spec's
 * `--ink-2` would emit as `--ink2` and every class written from the spec would miss.
 */
const kebab = (s: string) => s.replace(/([A-Z])|(\d+)/g, (_, c: string, d: string) => `-${c ? c.toLowerCase() : d}`);

/**
 * Emitted under a `--brand-` prefix rather than `--color-`, so these don't collide with Tailwind v4's
 * own `--color-*` namespace. `index.css` maps them into Tailwind via `@theme inline`, which keeps the
 * indirection intact — utilities resolve through the swappable variable rather than baking a value in.
 */
const block = (palette: SemanticPalette): string =>
  (Object.entries(palette) as [keyof SemanticPalette, string][])
    .map(([key, value]) => `    --brand-${kebab(key)}: ${value};`)
    .join('\n');

/**
 * Anything absent from the utility map takes its own kebab-cased name, which is now almost
 * everything: the whole point of moving to the brand board's vocabulary is that a token is called
 * the same thing in the palette, in the stylesheet and in a class.
 *
 * `focus` and `focusOnDeep` are excluded entirely — a focus ring is set by a base rule and
 * overridden per panel through `--focus-ring`, never written as a utility class.
 */
const NOT_UTILITIES: readonly (keyof SemanticPalette)[] = ['focus', 'focusOnDeep'];

/**
 * The `@theme inline` block, which is what actually lets `bg-deep` work.
 *
 * `inline` is doing the load-bearing work: it makes utilities resolve THROUGH `var(--brand-*)` at
 * runtime rather than baking the hex in at build time, which is what keeps a palette change to a
 * single edit of this file.
 *
 * Generated rather than hand-written in each app's stylesheet, because there are two apps. A
 * hand-maintained copy in each is one palette change away from the marketing site and the signed-in
 * app looking like different products.
 */
function themeBlock(): string {
  const colours = (Object.keys(light) as (keyof SemanticPalette)[])
    .filter((key) => !NOT_UTILITIES.includes(key))
    .map((key) => `    --color-${kebab(key)}: var(--brand-${kebab(key)});`)
    .join('\n');

  /**
   * `--radius-*` is Tailwind's own namespace, so mapping ours into it REPLACES the defaults:
   * `rounded` becomes 4px and `rounded-md` 6px because the brand says so, not because Tailwind
   * shipped those numbers. `rounded-card` and `rounded-chip` are new.
   */
  const radii = [
    `    --radius-chip: ${radius.chip};`,
    `    --radius: ${radius.base};`,
    `    --radius-card: ${radius.card};`,
    `    --radius-md: ${radius.panel};`,
  ].join('\n');

  return `@theme inline {
${colours}

${radii}

    --shadow-lifted: var(--brand-shadow-lifted);

    --font-display: var(--font-display);
    --font-body: var(--font-body);
    --font-ui: var(--font-ui);
    --font-mono: var(--font-mono);
}`;
}

/**
 * Generate the stylesheet consumed by Tailwind and shadcn.
 *
 * One theme. `color-scheme: light` is stated rather than left to default, so a browser in dark mode
 * does not tint form controls, scrollbars and autofill against a palette that has no dark variant.
 */
export function emitCssCustomProperties(): string {
  return `/* GENERATED FROM packages/shared/src/brand/tokens.ts — DO NOT EDIT BY HAND. */

:root {
${block(light)}

    --font-display: ${type.display};
    --font-body: ${type.body};
    --font-ui: ${type.ui};
    --font-mono: ${type.mono};

    --brand-shadow-lifted: ${shadows.lifted};

    color-scheme: light;
}

${themeBlock()}
`;
}
